import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ShoppingCart, Heart, MessageCircle, Shield, Truck, Award, Star, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ProductCard from '@/components/ProductCard';
import ProductReviews from '@/components/ProductReviews';
import { useProduct, useProducts } from '@/hooks/useProducts';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { getProductWhatsAppUrl } from '@/lib/whatsapp';
import { Skeleton } from '@/components/ui/skeleton';

// Parse color and storage from product name like "vivo T5x 5G (Star Silver, 128 GB) 6GB"
function parseVariants(name: string) {
  const parenMatch = name.match(/\(([^)]+)\)/);
  let color = '';
  let storage = '';
  if (parenMatch) {
    const parts = parenMatch[1].split(',').map(s => s.trim());
    // Last part that looks like storage (contains GB/TB)
    const storagePart = parts.find(p => /\d+\s*(GB|TB)/i.test(p));
    const colorPart = parts.find(p => !/\d+\s*(GB|TB)/i.test(p));
    if (storagePart) storage = storagePart;
    if (colorPart) color = colorPart;
  }
  // Also check for RAM suffix like "6GB" or "8GB" after the parens
  const ramMatch = name.match(/\)\s*(\d+GB)$/i);
  const ram = ramMatch ? ramMatch[1] : '';
  return { color, storage, ram };
}

// Get base model name (strip color/storage/ram)
function getBaseName(name: string) {
  return name.replace(/\s*\([^)]*\)\s*\d*GB?$/i, '').replace(/\s*\([^)]*\)/i, '').trim();
}

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading } = useProduct(id || '');
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedStorage, setSelectedStorage] = useState('');
  const [selectedRam, setSelectedRam] = useState('');

  // Reset image index when product changes
  useEffect(() => {
    setSelectedImage(0);
    setSelectedColor('');
    setSelectedStorage('');
    setSelectedRam('');
    // Force scroll to top when product loads
    window.scrollTo(0, 0);
  }, [id]);
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const { data: allProducts = [] } = useProducts(product?.category);

  // Find all variants of this product (same base model name)
  const variants = useMemo(() => {
    if (!product) return [];
    const base = getBaseName(product.name);
    return allProducts.filter(p => getBaseName(p.name) === base);
  }, [allProducts, product]);

  // Get unique colors and storages from variants
  const allColors = useMemo(() => [...new Set(variants.map(p => parseVariants(p.name).color).filter(Boolean))], [variants]);
  const allStorages = useMemo(() => [...new Set(variants.map(p => parseVariants(p.name).storage).filter(Boolean))], [variants]);
  const allRams = useMemo(() => [...new Set(variants.map(p => parseVariants(p.name).ram).filter(Boolean))], [variants]);

  const currentVariant = product ? parseVariants(product.name) : { color: '', storage: '', ram: '' };

  const related = allProducts.filter(p => p.id !== id && getBaseName(p.name) !== getBaseName(product?.name || '')).slice(0, 4);

  const handleVariantSelect = (color: string, storage: string, ram: string) => {
    // Find the product matching selected color+storage+ram
    const match = variants.find(p => {
      const v = parseVariants(p.name);
      return (!color || v.color === color) &&
             (!storage || v.storage === storage) &&
             (!ram || v.ram === ram);
    });
    if (match && match.id !== id) navigate(`/product/${match.id}`);
  };

  if (isLoading) {
    return <div className="container section-padding"><Skeleton className="h-96 w-full rounded-xl" /></div>;
  }

  if (!product) {
    return (
      <div className="container section-padding text-center">
        <p className="text-muted-foreground">Product not found.</p>
        <Link to="/products"><Button className="mt-4">Back to Products</Button></Link>
      </div>
    );
  }

  const liked = isInWishlist(product.id);
  const savings = product.original_price - product.price;
  const images = product.images?.length ? product.images : [product.image || '/placeholder.svg'];
  const specs = (product.specs || {}) as Record<string, string>;

  // Use stored variant arrays, fall back to parsing from name
  const storedColors = product.colors?.filter(Boolean) || [];
  const storedStorages = product.storages?.filter(Boolean) || [];
  const storedRams = product.rams?.filter(Boolean) || [];

  const displayColors = storedColors.length > 0 ? storedColors : allColors;
  const displayStorages = storedStorages.length > 0 ? storedStorages : allStorages;
  const displayRams = storedRams.length > 0 ? storedRams : allRams;

  const cartProduct = {
    id: product.id, name: product.name, brand: product.brand, category: product.category as any,
    price: product.price, originalPrice: product.original_price, discount: product.discount,
    image: product.image || '', images: product.images || [], rating: product.rating,
    reviews: product.reviews, description: product.description || '', specs,
    inStock: product.in_stock, isFlashSale: product.is_flash_sale, isFeatured: product.is_featured,
    selectedColor: selectedColor || currentVariant.color,
    selectedStorage: (selectedStorage || currentVariant.storage) + (selectedRam || currentVariant.ram ? ` ${selectedRam || currentVariant.ram}` : ''),
  };

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name, image: images, description: product.description,
    brand: { "@type": "Brand", name: product.brand },
    offers: { "@type": "Offer", price: product.price, priceCurrency: "INR", availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", seller: { "@type": "Organization", name: "Debnath Telecom & Variety Store" } },
    aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.reviews },
  };

  return (
    <>
      <Helmet>
        <title>{product.name} - Debnath Telecom</title>
        <meta name="description" content={product.description || ''} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container section-padding">
        <Link to="/products" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ChevronLeft className="h-4 w-4" /> Back to Products
        </Link>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="space-y-3">
            <div className="w-full h-[500px] bg-white rounded-lg border border-gray-100 overflow-hidden flex items-center justify-center p-4">
              <img
                key={images[selectedImage]}
                src={images[selectedImage] ?? '/placeholder.svg'}
                alt={product?.name ?? 'Product'}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.src = '/placeholder.svg';
                  target.onerror = null;
                }}
              />
            </div>
            {images.length > 1 && (
              <div className="flex gap-2">
                {images.map((img, i) => (
                  <button 
                    key={i} 
                    onClick={() => setSelectedImage(i)} 
                    className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-colors ${i === selectedImage ? 'border-accent' : 'border-transparent'}`}
                  >
                    <img 
                      src={img ?? '/placeholder.svg'} 
                      alt="" 
                      className="w-full h-full object-contain p-1 bg-white"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.src = '/placeholder.svg';
                        target.onerror = null;
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">{product.brand}</p>
              <h1 className="font-heading text-2xl md:text-3xl font-bold">{product.name}</h1>
            </div>

            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 fill-gold text-gold" />
              <span className="font-medium">{product.rating}</span>
              <span className="text-sm text-muted-foreground">({product.reviews.toLocaleString()} reviews)</span>
            </div>

            <div className="flex items-center gap-3">
              <span className="font-heading text-3xl font-bold">₹{product.price.toLocaleString('en-IN')}</span>
              {savings > 0 && (
                <>
                  <span className="text-lg text-muted-foreground line-through">₹{product.original_price.toLocaleString('en-IN')}</span>
                  <Badge className="bg-success/10 text-success border-success/30">Save ₹{savings.toLocaleString('en-IN')}</Badge>
                </>
              )}
            </div>

            <p className="text-muted-foreground">{product.description}</p>

            {/* Color Variants */}
            {displayColors.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Color: <span className="text-muted-foreground">{selectedColor || currentVariant.color || displayColors[0]}</span></p>
                <div className="flex flex-wrap gap-2">
                  {displayColors.map(color => (
                    <button
                      key={color}
                      onClick={() => {
                        setSelectedColor(color);
                        handleVariantSelect(color, selectedStorage || currentVariant.storage, selectedRam || currentVariant.ram);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-md border-2 transition-colors ${
                        (selectedColor || currentVariant.color) === color
                          ? 'border-primary bg-primary/5 font-medium'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Storage Variants */}
            {displayStorages.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Storage: <span className="text-muted-foreground">{selectedStorage || currentVariant.storage || displayStorages[0]}</span></p>
                <div className="flex flex-wrap gap-2">
                  {displayStorages.map(storage => (
                    <button
                      key={storage}
                      onClick={() => {
                        setSelectedStorage(storage);
                        handleVariantSelect(selectedColor || currentVariant.color, storage, selectedRam || currentVariant.ram);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-md border-2 transition-colors ${
                        (selectedStorage || currentVariant.storage) === storage
                          ? 'border-primary bg-primary/5 font-medium'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {storage}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* RAM Variants */}
            {displayRams.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">RAM: <span className="text-muted-foreground">{selectedRam || currentVariant.ram || displayRams[0]}</span></p>
                <div className="flex flex-wrap gap-2">
                  {displayRams.map(ram => (
                    <button
                      key={ram}
                      onClick={() => {
                        setSelectedRam(ram);
                        handleVariantSelect(selectedColor || currentVariant.color, selectedStorage || currentVariant.storage, ram);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-md border-2 transition-colors ${
                        (selectedRam || currentVariant.ram) === ram
                          ? 'border-primary bg-primary/5 font-medium'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {ram}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 py-2">
              <Button size="lg" className="flex-1 gap-2 font-heading font-semibold" onClick={() => addToCart(cartProduct)}>
                <ShoppingCart className="h-4 w-4" /> Add to Cart
              </Button>
              <a href={getProductWhatsAppUrl(product.name, product.price)} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button size="lg" variant="outline" className="w-full gap-2 border-success text-success hover:bg-success hover:text-primary-foreground font-heading font-semibold">
                  <MessageCircle className="h-4 w-4" /> Buy on WhatsApp
                </Button>
              </a>
              <Button size="lg" variant="outline" onClick={() => liked ? removeFromWishlist(product.id) : addToWishlist(cartProduct)}>
                <Heart className={`h-4 w-4 ${liked ? 'fill-destructive text-destructive' : ''}`} />
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-4 border-t">
              {[{ icon: Shield, label: 'Genuine Product' }, { icon: Truck, label: 'Fast Delivery' }, { icon: Award, label: 'Full Warranty' }].map(b => (
                <div key={b.label} className="flex items-center gap-2 text-xs text-muted-foreground"><b.icon className="h-4 w-4 text-primary" /><span>{b.label}</span></div>
              ))}
            </div>

            <div className="pt-4">
              <h3 className="font-heading font-semibold mb-3">Specifications</h3>
              <div className="border rounded-lg overflow-hidden">
                {Object.entries(specs).map(([key, val], i) => (
                  <div key={key} className={`flex text-sm ${i % 2 === 0 ? 'bg-muted/30' : ''}`}>
                    <span className="w-1/3 p-3 font-medium border-r">{key}</span>
                    <span className="flex-1 p-3 text-muted-foreground">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mb-16">
          <ProductReviews productId={product.id} productName={product.name} />
        </div>

        {related.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-bold mb-6">Related Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductDetailPage;
